'use client';

import React, { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';

const STUDIO_NAME = 'REGAL PORTRAITS';
const SUBTITLE = 'Photography packages now booking';

export default function HeroSection({ onBookNow }: { onBookNow?: () => void }) {
  const [lettersShown, setLettersShown] = useState(0);
  const [subtitleVisible, setSubtitleVisible] = useState(false);
  const hasStarted = useRef(false);

  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    const delay = 800;
    const interval = 90;

    const timer = setTimeout(() => {
      let i = 0;
      const ticker = setInterval(() => {
        i++;
        setLettersShown(i);
        if (i >= STUDIO_NAME.length) {
          clearInterval(ticker);
          setTimeout(() => setSubtitleVisible(true), 400);
        }
      }, interval);
    }, delay);

    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative w-full h-screen min-h-[600px] overflow-hidden">
      {/* Background photo */}
      <div className="absolute inset-0">
        <AppImage
          src="https://images.unsplash.com/photo-1554048612-b6a482bc67e5"
          alt="Photographer in a dramatic studio setting, camera raised, golden light casting long shadows across the floor"
          fill
          priority
          className="object-cover"
          style={{ filter: 'brightness(0.4) saturate(0.6) contrast(1.1)' } as React.CSSProperties}
        />
      </div>

      {/* Atmospheric overlays */}
      <div className="hero-overlay absolute inset-0" />

      {/* Gold vignette edge */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 50% 110%, rgba(139,105,20,0.12) 0%, transparent 60%)',
        }}
      />

      {/* Noise grain overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.035]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          backgroundSize: '128px 128px',
        }}
      />

      {/* Center content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
        {/* Studio name letter by letter */}
        <h1
          className="font-display font-black tracking-[0.35em] select-none"
          style={{
            fontSize: 'clamp(2rem, 6vw, 4.5rem)',
            color: '#D4A843',
            lineHeight: 1.15,
            textShadow: '0 0 60px rgba(212,168,67,0.3), 0 0 120px rgba(212,168,67,0.12)',
            letterSpacing: '0.35em',
          }}
        >
          <span className="block">
            {'REGAL'.split('').map((letter, i) => (
              <span
                key={`r-${i}`}
                className="marquee-letter"
                style={{
                  animationDelay: `${i * 0.09}s`,
                  animationPlayState: i < lettersShown ? 'running' : 'paused',
                  opacity: i < lettersShown ? undefined : 0,
                }}
              >
                {letter}
              </span>
            ))}
          </span>
          <span className="block">
            {'PORTRAITS'.split('').map((letter, i) => (
              <span
                key={`p-${i}`}
                className="marquee-letter"
                style={{
                  animationDelay: `${(i + 6) * 0.09}s`,
                  animationPlayState: (i + 6) < lettersShown ? 'running' : 'paused',
                  opacity: (i + 6) < lettersShown ? undefined : 0,
                }}
              >
                {letter}
              </span>
            ))}
          </span>
        </h1>

        {/* Subtitle */}
        <p
          className={`subtitle-reveal mt-6 font-sans font-light`}
          style={{
            fontSize: 'clamp(0.7rem, 2vw, 0.9rem)',
            color: '#E8E4DC',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            opacity: subtitleVisible ? undefined : 0,
            animationPlayState: subtitleVisible ? 'running' : 'paused',
          }}
        >
          {SUBTITLE}
        </p>

        {/* Book Now CTA */}
        <div
          className="mt-10"
          style={{
            opacity: subtitleVisible ? 1 : 0,
            transition: 'opacity 0.8s ease 0.6s',
          }}
        >
          <button
            className="btn-gold px-10 py-4 text-sm"
            onClick={onBookNow}
          >
            <span>Book Now</span>
          </button>
        </div>

        {/* Scroll cue */}
        <div
          className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3"
          style={{ opacity: subtitleVisible ? 0.5 : 0, transition: 'opacity 1s ease 0.5s' }}
        >
          <span
            className="text-[9px] font-bold uppercase tracking-[0.4em]"
            style={{ color: '#D4A843' }}
          >
            Explore
          </span>
          <div
            className="w-px h-12 relative overflow-hidden"
            style={{ background: 'rgba(212,168,67,0.2)' }}
          >
            <div
              className="absolute top-0 left-0 w-full"
              style={{
                height: '40%',
                background: 'linear-gradient(to bottom, #D4A843, transparent)',
                animation: 'scrollLine 2s ease-in-out infinite',
              }}
            />
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes scrollLine {
          0% { transform: translateY(-100%); opacity: 1; }
          100% { transform: translateY(300%); opacity: 0; }
        }
      `}</style>
    </section>
  );
}